import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  APP_INITIALIZER,
  DestroyRef,
  EnvironmentProviders,
  PLATFORM_ID,
  makeEnvironmentProviders
} from '@angular/core';
import { WorkerSynchronizer, WorkerSynchronizerSpawner } from '@bcf-v2-platforms/platform-worker/worker-synchronizer';
import { getDestroySubjectFromRef } from '@bcf-v2-shared/misc/rx-helpers/get-destroy-subject-from-ref';
import { Observable, Subject, fromEvent, map, mapTo, merge, mergeMap, startWith, takeUntil, timer } from 'rxjs';

function visibilityChangeEvent(document: Document): string {
  if (typeof document.hidden !== 'undefined') {
    return 'visibilitychange';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    return 'msvisibilitychange';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    return 'webkitvisibilitychange';
  }
  return 'visibilitychange';
}

function documentHiddenProp(document: Document): string {
  if (typeof document.hidden !== 'undefined') {
    return 'hidden';
  } else if (typeof (document as any).msHidden !== 'undefined') {
    return 'msHidden';
  } else if (typeof (document as any).webkitHidden !== 'undefined') {
    return 'webkitHidden';
  }
  return 'hidden';
}

function getPageActiveState(document: Document): Observable<boolean> {
  // timer because we dont need this at startup - perf, but timer set to 0 if the page initially is not active
  const documentAny: any = document;
  const delayInMilliseconds: number = documentAny[documentHiddenProp(document)] ? 0 : 5000;
  return timer(delayInMilliseconds).pipe(
    mergeMap(() =>
      merge(
        fromEvent(document, visibilityChangeEvent(document)).pipe(
          map(() => !documentAny[documentHiddenProp(document)])
        ),
        fromEvent(document, 'pause').pipe(mapTo(false)),
        fromEvent(document, 'resume').pipe(mapTo(true))
      )
    ),
    startWith(!documentAny[documentHiddenProp(document)])
  );
}

export function provideAppVisibilityBrowserProvider(): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: APP_INITIALIZER,
      useFactory:
        (
          document: Document,
          platformId: string,
          workerSynchronizerSpawner: WorkerSynchronizerSpawner,
          destroyRef: DestroyRef
        ) =>
        () => {
          if (isPlatformServer(platformId)) {
            return;
          }
          const appVisibilityChannel: WorkerSynchronizer<boolean> =
            workerSynchronizerSpawner.spawn('appVisibilityChannel');

          const destroy$: Subject<void> = getDestroySubjectFromRef(destroyRef);
          getPageActiveState(document)
            .pipe(takeUntil(destroy$))
            .subscribe((state: boolean) => appVisibilityChannel.next(state));
        },
      deps: [DOCUMENT, PLATFORM_ID, WorkerSynchronizerSpawner, DestroyRef],
      multi: true
    }
  ]);
}
